'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import { mdiClose } from '@mdi/js'
import Icon from '@mdi/react'
import moment from 'moment'
import React from 'react'

// walrus
import Box from '@components/kit/box'
import { BoxLink } from '@components/kit/link'
import Pressable from '@components/kit/pressable'
import Text from '@components/kit/text'

// lib
import BadgePill from '@components/ui/badge-pill'
import DataTable, { DataTableColumn } from '@components/ui/data-table'
import PriorityTag from '@components/ui/priority-tag'
import SectionHeader from '@components/ui/section-header'
import StatCard from '@components/ui/stat-card'
import { IMemberOpportunity } from '@store/MemberOpportunities'
import { colors } from '@theme'
import { FilterPillGroup } from './filter-pill'
import {
  ALL_FILTER,
  getOpportunityTypeStyle,
  Priority,
  PRIORITIES,
  PRIORITY_KEYS
} from './data/taxonomy'
import { describeOpportunity, formatMoney } from './opportunity-text'

/* -----------------------------------------------------------------------------
 * constants
 * -------------------------------------------------------------------------- */

// days since an opportunity was flagged past which its age reads as overdue
const STALE_DAYS = 10

const PRIORITY_FILTER_OPTIONS = [ALL_FILTER, ...PRIORITY_KEYS].map(value => ({
  label: value,
  value
}))

// Message is the row's SEND_TO_MEMBER action: the chat opens with the draft,
// and the delivered message marks the opportunity sent. A row that comes
// without `actions` hides Message instead of breaking the whole table
const canMessage = (opportunity: IMemberOpportunity) =>
  opportunity.actions?.some(item => item.action === 'SEND_TO_MEMBER') ?? false

/* -----------------------------------------------------------------------------
 * LoadMoreFooter
 * -------------------------------------------------------------------------- */

type LoadMoreFooterProps = {
  isFetchingNextPage: boolean
  onLoadNextPage: () => void
}

function LoadMoreFooter({
  isFetchingNextPage,
  onLoadNextPage
}: LoadMoreFooterProps) {
  return (
    <Box
      alignItems='center'
      borderColor='proDividerSoft'
      borderStyle='solid'
      borderTopWidth={1}
      py='12'
    >
      <Pressable disabled={isFetchingNextPage} onPress={onLoadNextPage}>
        <Text font='pro.text.12.semibold' textColor='proAccent'>
          {isFetchingNextPage ? 'Loading…' : 'Load more'}
        </Text>
      </Pressable>
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * OpportunitiesSection
 * -------------------------------------------------------------------------- */

export type TypeTile = {
  // null until its total arrives, or when it could not be read
  count: number | null
  type: string
}

export type OpportunitiesSectionProps = {
  hasFailed: boolean
  hasNextPage: boolean
  isFetchingNextPage: boolean
  isFiltered: boolean
  isLoading: boolean
  onLoadNextPage: () => void
  onMessage: (opportunity: IMemberOpportunity) => void
  onPriorityFilter: (value: string) => void
  onReject: (opportunity: IMemberOpportunity) => void
  onResetFilters: () => void
  onRetry: () => void
  onTypeFilter: (value: string) => void
  priorityFilter: string
  resultCount: number
  rows: IMemberOpportunity[]
  typeFilter: string
  typeTiles: TypeTile[]
}

function OpportunitiesSection({
  hasFailed,
  hasNextPage,
  isFetchingNextPage,
  isFiltered,
  isLoading,
  onLoadNextPage,
  onMessage,
  onPriorityFilter,
  onReject,
  onResetFilters,
  onRetry,
  onTypeFilter,
  priorityFilter,
  resultCount,
  rows,
  typeFilter,
  typeTiles
}: OpportunitiesSectionProps) {
  const columns: DataTableColumn<IMemberOpportunity>[] = [
    {
      key: 'user_id',
      label: 'Member',
      render: row =>
        row.user_id ? (
          <BoxLink path={`/chat/${row.user_id}`}>
            <Text font='pro.text.13.medium' textColor='proAccent'>
              {row.user_id}
            </Text>
          </BoxLink>
        ) : null,
      width: 90
    },
    {
      flex: 1.1,
      key: 'opportunity_type',
      label: 'Opportunity',
      minWidth: 160,
      render: row => {
        const style = getOpportunityTypeStyle(row.opportunity_type)
        return (
          <BadgePill
            bgColor={style.bgColor}
            dotColor={style.dotColor}
            label={style.label}
            labelColor={style.labelColor}
          />
        )
      }
    },
    {
      flex: 1,
      key: 'drug_name',
      label: 'Medication',
      minWidth: 140,
      render: row => (
        <Text font='pro.text.13.medium' textColor='proInk'>
          {row.drug_name}
        </Text>
      )
    },
    {
      flex: 1.5,
      key: 'detail',
      label: 'Detail',
      minWidth: 145,
      render: row => (
        <Text font='pro.text.13.regular' textColor='proTextMuted'>
          {describeOpportunity(row)}
        </Text>
      )
    },
    {
      key: 'priority',
      label: 'Priority',
      render: row => {
        const style = PRIORITIES[row.priority as Priority]
        return style ? (
          <PriorityTag
            bgColor={style.bgColor}
            label={row.priority}
            labelColor={style.labelColor}
          />
        ) : (
          <Text font='pro.text.13.regular' textColor='proTextMuted'>
            {row.priority}
          </Text>
        )
      },
      width: 100
    },
    {
      key: 'total_estimated_annual_savings',
      label: 'Savings',
      render: row => (
        <Text font='pro.text.13.medium' textColor='proTealDeep'>
          {row.total_estimated_annual_savings > 0
            ? `${formatMoney(row.total_estimated_annual_savings)}/yr`
            : ''}
        </Text>
      ),
      width: 100
    },
    {
      key: '_created',
      label: 'Flagged',
      render: row => {
        if (!row._created) {
          return null
        }
        const days = moment().diff(moment(row._created), 'days')
        const isStale = days >= STALE_DAYS
        return (
          <Text
            font={isStale ? 'pro.text.13.medium' : 'pro.text.13.regular'}
            textColor={isStale ? 'proDanger' : 'proTextMuted'}
          >
            {days === 0 ? 'Today' : `${days}d ago`}
          </Text>
        )
      },
      width: 95
    },
    {
      align: 'flex-end',
      key: 'actions',
      label: 'Action',
      render: row =>
        row.user_id && canMessage(row) ? (
          <Pressable
            bg='proAccent'
            borderRadius='proSm'
            onPress={() => onMessage(row)}
            px='16'
            py='08'
          >
            <Text font='pro.text.12.semibold' textColor='white'>
              Message
            </Text>
          </Pressable>
        ) : null,
      width: 130
    },
    {
      align: 'flex-end',
      key: 'reject',
      label: 'Reject',
      render: row =>
        row.user_id ? (
          <Pressable
            accessibilityLabel='Reject'
            accessibilityRole='button'
            borderRadius='proSm'
            onPress={() => onReject(row)}
            p='04'
          >
            <Icon path={mdiClose} size='18px' color={colors.proDangerDeep} />
          </Pressable>
        ) : null,
      width: 64
    }
  ]

  return (
    <Box mb='40'>
      <SectionHeader
        count={resultCount}
        description='Flagged from claims data, by opportunity type'
        title='Member opportunities'
      />

      <Box flexDirection='row' flexWrap='wrap' gap='12' mb='20'>
        {typeTiles.map(({ count, type }) => {
          const style = getOpportunityTypeStyle(type)
          return (
            <StatCard
              count={count ?? '-'}
              dotColor={style.dotColor}
              flex='1 1 158px'
              key={type}
              label={style.label}
              minWidth='158px'
              onPress={() =>
                onTypeFilter(typeFilter === type ? ALL_FILTER : type)
              }
              selected={typeFilter === type}
            />
          )
        })}
      </Box>

      <FilterPillGroup
        mb='16'
        onSelect={onPriorityFilter}
        options={PRIORITY_FILTER_OPTIONS}
        value={priorityFilter}
      />

      <DataTable<IMemberOpportunity>
        columns={columns}
        data={rows}
        emptyActionLabel={
          hasFailed ? 'Try again' : isFiltered ? 'Reset filters' : undefined
        }
        emptyDescription={
          hasFailed
            ? 'The opportunities could not be loaded'
            : isFiltered
            ? 'No opportunities match these filters'
            : 'New opportunities show up here once they are flagged'
        }
        emptyTitle={hasFailed ? 'Something went wrong' : 'Queue clear'}
        footer={
          hasNextPage ? (
            <LoadMoreFooter
              isFetchingNextPage={isFetchingNextPage}
              onLoadNextPage={onLoadNextPage}
            />
          ) : null
        }
        getRowKey={row => String(row._id)}
        isLoading={isLoading}
        onEmptyAction={
          hasFailed ? onRetry : isFiltered ? onResetFilters : undefined
        }
        rowBgColor={row =>
          row.priority === 'High' ? 'proRowAlert' : 'proSurface'
        }
      />
    </Box>
  )
}

export default OpportunitiesSection
